import { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Button, Form, Row, Col, Tab, Nav } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";

import { api_without_cancellation } from "../../api/api";
import { getCaseId, getClientId, formatDateForPanelDisplay } from "../../Utils/helper";
import SelectStateModal from "../TreatmentPage/modals/state-modal/SelectStateModal";

import { EditLoanApi } from "./common/addEditLoanApi";
import deleteEditLoanApi from "./common/deleteEditLoanApi";

const EditCaseLoanModal = ({ loan, show, handleClose, updateLoans }) => {
  const [statesAbrs, setStatesAbrs] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // eslint-disable-line
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false); // eslint-disable-line
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Formatted display state for phone/fax fields (company + contact)
  const [formattedCompanyPhone, setFormattedCompanyPhone] = useState("");
  const [formattedCompanyFax, setFormattedCompanyFax] = useState("");
  const [formattedContactPhone, setFormattedContactPhone] = useState("");
  const [formattedContactFax, setFormattedContactFax] = useState("");

  // State selectors
  const [companyStateShow, setCompanyStateShow] = useState(false);
  const [selectedCompanyState, setSelectedCompanyState] = useState("");
  const [contactStateShow, setContactStateShow] = useState(false);
  const [selectedContactState, setSelectedContactState] = useState("");

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const formatPhoneNumberForDisplay = (value) => {
    if (!value) return "";
    const phoneNumber = value.replace(/[^\d]/g, "").slice(0, 10);
    if (phoneNumber.length < 4) return `(${phoneNumber}`;
    if (phoneNumber.length < 7) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
  };

  const makePhoneHandler = (fieldName, setFormatted) => (e) => {
    const rawDigits = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
    formik.setFieldValue(fieldName, rawDigits);
    setFormatted(formatPhoneNumberForDisplay(rawDigits));
  };

  const handleCurrencyChange = (e, fieldName) => {
    let value = e.target.value.replace(/[^0-9.]/g, "");
    if ((value.match(/\./g) || []).length > 1) {
      value = value.substring(0, value.lastIndexOf("."));
    }
    if (value.includes(".")) {
      const parts = value.split(".");
      if (parts[1].length > 2) parts[1] = parts[1].substring(0, 2);
      value = parts.join(".");
    }
    formik.setFieldValue(fieldName, value);
  };

  const handleCurrencyBlur = (fieldName) => {
    const raw = parseFloat(formik.values[fieldName] || 0);
    formik.setFieldValue(fieldName, isNaN(raw) ? "0.00" : raw.toFixed(2));
  };

  const currencyDisplay = (fieldName) => {
    const val = formik.values[fieldName];
    return val ? `$ ${val}` : "$ ";
  };

  const stateSelector = (selectedValue, onClick) => (
    <div className="position-relative height-25 custom-select-new-provider">
      <div
        className="dropdown-button rounded-0 p-0 p-l-5 form-control height-25 d-flex align-items-center"
        onClick={onClick}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
      >
        <span>
          {selectedValue ? (
            <div className="d-flex align-items-center">
              <svg
                style={{
                  width: "15px",
                  height: "15px",
                  fill: "var(--primary-80)",
                  stroke: "var(--primary-80)",
                }}
                className={`icon icon-state-${selectedValue}`}
              >
                <use xlinkHref={`#icon-state-${selectedValue}`} />
              </svg>
              {selectedValue}
            </div>
          ) : (
            "State"
          )}
        </span>
      </div>
    </div>
  );

  // ─── Data Fetch ────────────────────────────────────────────────────────────

  const fetchStatesData = async () => {
    try {
      // const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";
      const response = await api_without_cancellation.get("/api/states/");
      if (response.status === 200) setStatesAbrs(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchStatesData();
  }, []);

  // ─── Formik ────────────────────────────────────────────────────────────────

  const formik = useFormik({
    initialValues: {
      // Loan Company
      loan_company: "",
      company_address1: "",
      company_address2: "",
      company_city: "",
      company_state: "",
      company_zip_code: "",
      company_phone: "",
      company_extension: "",
      company_fax: "",
      company_email: "",
      company_website: "",

      // Loan Contact
      first_name: "",
      last_name: "",
      contact_address1: "",
      contact_address2: "",
      contact_city: "",
      contact_state: "",
      contact_zip_code: "",
      contact_phone: "",
      contact_extension: "",
      contact_fax: "",
      contact_email: "",
      contact_website: "",

      // Loan Details
      current_amount_verified: "",
      date_disbursed: null,
      fees: "",
      interest: "",
      amount_estimate: "",
      date_verified: null,
      final_payoff: "",
    },
    validationSchema: Yup.object({
      loan_company: Yup.string().nullable(),
      company_address1: Yup.string().nullable(),
      company_address2: Yup.string().nullable(),
      company_city: Yup.string().nullable(),
      company_state: Yup.string().nullable(),
      company_zip_code: Yup.string().nullable(),
      company_phone: Yup.string().nullable(),
      company_extension: Yup.string().nullable(),
      company_fax: Yup.string().nullable(),
      company_email: Yup.string().email("Invalid email").nullable(),
      company_website: Yup.string().nullable(),
      first_name: Yup.string().nullable(),
      last_name: Yup.string().nullable(),
      contact_address1: Yup.string().nullable(),
      contact_address2: Yup.string().nullable(),
      contact_city: Yup.string().nullable(),
      contact_state: Yup.string().nullable(),
      contact_zip_code: Yup.string().nullable(),
      contact_phone: Yup.string().nullable(),
      contact_extension: Yup.string().nullable(),
      contact_fax: Yup.string().nullable(),
      contact_email: Yup.string().email("Invalid email").nullable(),
      contact_website: Yup.string().nullable(),
      current_amount_verified: Yup.number().nullable(),
      date_disbursed: Yup.date().nullable(),
      fees: Yup.number().nullable(),
      interest: Yup.number().nullable(),
      amount_estimate: Yup.number().nullable(),
      date_verified: Yup.date().nullable(),
      final_payoff: Yup.number().nullable(),
    }),
    onSubmit: async (values) => {
      const transformedValues = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          value === null ? null : value.toString(),
        ])
      );

      const payload = {
        case_id: getCaseId(),
        client_id: getClientId(),

        // Company
        loan_id: transformedValues?.loan_id || null,
        loan_company_id: transformedValues?.loan_company_id || null,
        loan_company: transformedValues.loan_company,
        company_address1: transformedValues.company_address1,
        company_address2: transformedValues.company_address2,
        company_city: transformedValues.company_city,
        company_state: transformedValues.company_state,
        company_zip_code: transformedValues.company_zip_code,
        company_phone: transformedValues.company_phone,
        company_extension: transformedValues.company_extension,
        company_fax: transformedValues.company_fax,
        company_email: transformedValues.company_email,
        company_website: transformedValues.company_website,

        // Contact
        first_name: transformedValues.first_name,
        last_name: transformedValues.last_name,
        contact_address1: transformedValues.contact_address1,
        contact_address2: transformedValues.contact_address2,
        contact_city: transformedValues.contact_city,
        contact_state: transformedValues.contact_state,
        contact_zip_code: transformedValues.contact_zip_code,
        contact_phone: transformedValues.contact_phone,
        contact_extension: transformedValues.contact_extension,
        contact_fax: transformedValues.contact_fax,
        contact_email: transformedValues.contact_email,
        contact_website: transformedValues.contact_website,

        // Details
        current_amount_verified: transformedValues.current_amount_verified || null,
        date_disbursed: transformedValues.date_disbursed || null,
        fees: transformedValues.fees || null,
        interest: transformedValues.interest || null,
        amount_estimate: transformedValues.amount_estimate || null,
        date_verified: transformedValues.date_verified || null,
        final_payoff: transformedValues.final_payoff || null,
      };

      await EditLoanApi(payload);
      updateLoans();
      handleClose();
    },
  });

  // ─── Search ────────────────────────────────────────────────────────────────

  const handleSearch = async (value) => {
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await api_without_cancellation.get(
        `/api/add/case/loan/${getClientId()}/${getCaseId()}/`,
        { params: { q: value } }
      );
      if (response.status === 200) setSearchResults(response.data.data);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultSelect = (result) => {
    const c = result.contact || {};

    // Company fields from search result
    formik.setFieldValue("loan_company_id", result.id || "");
    formik.setFieldValue("loan_company", result.loan_company || "");
    formik.setFieldValue("company_address1", c.address1 || "");
    formik.setFieldValue("company_address2", c.address2 || "");
    formik.setFieldValue("company_city", c.city || "");
    formik.setFieldValue("company_state", c.state || "");
    formik.setFieldValue("company_zip_code", c.zip || "");
    formik.setFieldValue("company_phone", c.phone_number || "");
    formik.setFieldValue("company_extension", c.phone_ext || "");
    formik.setFieldValue("company_fax", c.fax || "");
    formik.setFieldValue("company_email", c.email || "");
    formik.setFieldValue("company_website", c.website || "");
    setSelectedCompanyState(c.state || "");
    setFormattedCompanyPhone(formatPhoneNumberForDisplay(c.phone_number || ""));
    setFormattedCompanyFax(formatPhoneNumberForDisplay(c.fax || ""));

    setSearchResults([]);
    setSearchTerm("");
    setIsFiltersOpen(false);
  };

  const debounce = (func, delay) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const debouncedSearch = useCallback(debounce(handleSearch, 300), []);
  // Add ref at the top of your component with other state
  const searchDropdownRef = useRef(null);

  // Add this useEffect
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(e.target)) {
        setIsFiltersOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  // ─── JSX ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!loan || !show) return;

    const company = loan.lending_company_contact || {};
    const contact = loan.contact || {};
    formik.setFieldValue("loan_id", loan.id || null);
    // Company fields
    formik.setFieldValue("loan_company_id", loan.directory_id || null);
    formik.setFieldValue("loan_company", company.name || "");
    formik.setFieldValue("company_address1", company.address1 || "");
    formik.setFieldValue("company_address2", company.address2 || "");
    formik.setFieldValue("company_city", company.city || "");
    formik.setFieldValue("company_state", company.state || "");
    formik.setFieldValue("company_zip_code", company.zip || "");
    formik.setFieldValue("company_phone", company.phone_number || "");
    formik.setFieldValue("company_extension", company.phone_ext || "");
    formik.setFieldValue("company_fax", company.fax || "");
    formik.setFieldValue("company_email", company.email || "");
    formik.setFieldValue("company_website", company.website || "");
    setSelectedCompanyState(company.state || "");
    setFormattedCompanyPhone(formatPhoneNumberForDisplay(company.phone_number || ""));
    setFormattedCompanyFax(formatPhoneNumberForDisplay(company.fax || ""));
    setSearchTerm(company.name || "");

    // Contact fields
    formik.setFieldValue("first_name", contact.first_name || "");
    formik.setFieldValue("last_name", contact.last_name || "");
    formik.setFieldValue("contact_address1", contact.address1 || "");
    formik.setFieldValue("contact_address2", contact.address2 || "");
    formik.setFieldValue("contact_city", contact.city || "");
    formik.setFieldValue("contact_state", contact.state || "");
    formik.setFieldValue("contact_zip_code", contact.zip || "");
    formik.setFieldValue("contact_phone", contact.phone_number || "");
    formik.setFieldValue("contact_extension", contact.phone_ext || "");
    formik.setFieldValue("contact_fax", contact.fax || "");
    formik.setFieldValue("contact_email", contact.email || "");
    formik.setFieldValue("contact_website", contact.website || "");
    setSelectedContactState(contact.state || "");
    setFormattedContactPhone(formatPhoneNumberForDisplay(contact.phone_number || ""));
    setFormattedContactFax(formatPhoneNumberForDisplay(contact.fax || ""));

    // Loan details
    formik.setFieldValue("current_amount_verified", loan.current_amount_verified?.toString() || "");
    formik.setFieldValue(
      "date_disbursed",
      loan.date_disbursed ? loan.date_disbursed.split("T")[0] : null
    );
    formik.setFieldValue("fees", loan.fees?.toString() || "");
    formik.setFieldValue("interest", loan.interest?.toString() || "");
    formik.setFieldValue("amount_estimate", loan.amount_estimate?.toString() || "");
    formik.setFieldValue(
      "date_verified",
      loan.date_verified ? loan.date_verified.split("T")[0] : null
    );
    formik.setFieldValue("final_payoff", loan.final_amount?.toString() || "");
  }, [loan, show]);
  const handleDeleteLoan = async () => {
    try {
      await deleteEditLoanApi(loan?.id);
      updateLoans();
      handleClose();
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="custom-insurance-dialog justify-content-center"
        style={{ opacity: companyStateShow || contactStateShow ? "0.5" : "1" }}
      >
        <div className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center modal-header">
          <div
            className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center modal-title h4"
            style={{ fontSize: "14px", fontWeight: "600" }}
          >
            Edit Case Loan
          </div>
        </div>

        <Modal.Body className="p-0">
          <Tab.Container defaultActiveKey="loanCompany">
            <Nav
              variant="tabs"
              className="justify-content-around"
              style={{ marginTop: "5px", marginBottom: "5px" }}
            >
              <Nav.Link
                className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite apply-before"
                eventKey="loanCompany"
              >
                Lender Company
              </Nav.Link>
              <Nav.Link
                className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite"
                eventKey="loanContact"
              >
                Lender Contact
              </Nav.Link>
              <Nav.Link
                className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite apply-after"
                eventKey="loanDetails"
              >
                Loan Details
              </Nav.Link>
            </Nav>

            <Form onSubmit={formik.handleSubmit}>
              <Tab.Content>
                {/* ─── LOAN COMPANY TAB ─── */}
                <Tab.Pane
                  style={{
                    minHeight: "225px",
                  }}
                  eventKey="loanCompany"
                >
                  {/* Search */}
                  <Row className="mx-0">
                    <Col md={12} className="p-l-5 p-r-5 m-b-5" ref={searchDropdownRef}>
                      <div className="position-relative">
                        <input
                          type="text"
                          placeholder="Search Loan Companies"
                          value={searchTerm}
                          className="form-control custom-margin-bottom p-l-5 p-0 mb-0 rounded-0 height-25"
                          style={{ paddingRight: "24px" }}
                          onChange={(e) => {
                            const value = e.target.value;
                            setIsFiltersOpen(true);
                            setSearchTerm(value);
                            debouncedSearch(value);
                          }}
                        />
                        {searchTerm && (
                          <span
                            onClick={() => {
                              setSearchTerm("");
                              setSearchResults([]);
                              setIsFiltersOpen(false);
                              // Clear the selected company fields
                              formik.setFieldValue("loan_company", "");
                              formik.setFieldValue("company_address1", "");
                              formik.setFieldValue("company_address2", "");
                              formik.setFieldValue("company_city", "");
                              formik.setFieldValue("company_state", "");
                              formik.setFieldValue("company_zip_code", "");
                              formik.setFieldValue("company_phone", "");
                              formik.setFieldValue("company_extension", "");
                              formik.setFieldValue("company_fax", "");
                              formik.setFieldValue("company_email", "");
                              formik.setFieldValue("company_website", "");
                              setSelectedCompanyState("");
                              setFormattedCompanyPhone("");
                              setFormattedCompanyFax("");
                            }}
                            style={{
                              position: "absolute",
                              right: "10px",
                              top: "5px",
                              // transform: "translateY(-50%)",
                              cursor: "pointer",
                              color: "var(--primary)",
                              fontWeight: "bold",
                              fontSize: "14px",
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </span>
                        )}
                      </div>

                      {Array.isArray(searchResults) &&
                        searchResults.length > 0 &&
                        isFiltersOpen && (
                          <div style={{ position: "relative" }}>
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 1000,
                                backgroundColor: "#fff",
                                border: "1px solid #ccc",
                                width: "100%",
                                minHeight: "200px",
                                maxHeight: "200px",
                                overflowY: "auto",
                              }}
                            >
                              {searchResults.map((result, index) => (
                                <div
                                  key={index}
                                  onClick={() => {
                                    handleResultSelect(result);
                                    setSearchTerm(result?.loan_company || ""); // ← show selected in input
                                  }}
                                  style={{
                                    height: "25px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #ddd",
                                  }}
                                  className="d-flex align-items-center justify-content-start p-l-5 p-r-5"
                                >
                                  {result?.loan_company} {" - "}
                                  {result?.contact?.address1} {result?.contact?.city}{" "}
                                  {result?.contact?.state} {result?.contact?.zip}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </Col>
                  </Row>

                  {/* Company Name */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col sm={12} className="p-l-5 p-r-5">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Loan Company"
                        name="loan_company"
                        value={formik.values.loan_company}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>

                  {/* Company Address */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col sm={6} className="p-l-5 p-r-0">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Address 1"
                        name="company_address1"
                        value={formik.values.company_address1}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col sm={6} className="p-r-5 p-l-5">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Address 2"
                        name="company_address2"
                        value={formik.values.company_address2}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>

                  {/* Company City / State / Zip */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col className="flex-1 p-l-5 pe-0">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="City"
                        name="company_city"
                        value={formik.values.company_city}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col
                      style={{
                        width: "90px",
                        maxWidth: "90px",
                      }}
                      className="custom-select-state-entity p-r-0 p-l-5"
                    >
                      {stateSelector(selectedCompanyState, () => setCompanyStateShow(true))}
                    </Col>
                    <Col
                      style={{
                        width: "110px",
                        maxWidth: "110px",
                      }}
                      className="p-r-5 p-l-5"
                    >
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Zip"
                        name="company_zip_code"
                        value={formik.values.company_zip_code}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>

                  {/* Company Phone / Ext / Fax */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col className="flex-g-1 p-l-5 pe-0">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Phone: (###) ###-####"
                        name="company_phone"
                        value={formattedCompanyPhone}
                        onChange={makePhoneHandler("company_phone", setFormattedCompanyPhone)}
                      />
                    </Col>
                    <Col
                      style={{
                        maxWidth: "110px",
                        width: "110px",
                      }}
                      className="p-r-0 p-l-5"
                    >
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Ext"
                        name="company_extension"
                        value={formik.values.company_extension}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col className="flex-g-1 p-r-5 p-l-5">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Fax: (###) ###-####"
                        name="company_fax"
                        value={formattedCompanyFax}
                        onChange={makePhoneHandler("company_fax", setFormattedCompanyFax)}
                      />
                    </Col>
                  </Row>

                  {/* Company Email / Website */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col sm={12} className="p-l-5 p-r-5">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Email: someone@lender.com"
                        name="company_email"
                        value={formik.values.company_email}
                        onChange={formik.handleChange}
                        isInvalid={formik.touched.company_email && !!formik.errors.company_email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.company_email}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col sm={12} className="p-l-5 p-r-5">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Website: www.lender.com"
                        name="company_website"
                        value={formik.values.company_website}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>
                </Tab.Pane>

                {/* ─── LOAN CONTACT TAB ─── */}
                <Tab.Pane
                  style={{
                    minHeight: "225px",
                  }}
                  eventKey="loanContact"
                >
                  {/* First / Last Name */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col sm={6} className="p-l-5 p-r-0">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="First Name"
                        name="first_name"
                        value={formik.values.first_name}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col sm={6} className="p-r-5 p-l-5">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Last Name"
                        name="last_name"
                        value={formik.values.last_name}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>

                  {/* Contact Address */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col sm={6} className="p-l-5 p-r-0">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Address 1"
                        name="contact_address1"
                        value={formik.values.contact_address1}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col sm={6} className="p-r-5 p-l-5">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Address 2"
                        name="contact_address2"
                        value={formik.values.contact_address2}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>

                  {/* Contact City / State / Zip */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col className="flex-g-1 p-l-5 pe-0">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="City"
                        name="contact_city"
                        value={formik.values.contact_city}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col
                      style={{
                        width: "90px",
                        maxWidth: "90px",
                      }}
                      className="custom-select-state-entity p-r-0 p-l-5"
                    >
                      {stateSelector(selectedContactState, () => setContactStateShow(true))}
                    </Col>
                    <Col
                      style={{
                        width: "110px",
                        maxWidth: "110px",
                      }}
                      className="p-r-5 p-l-5"
                    >
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Zip Code"
                        name="contact_zip_code"
                        value={formik.values.contact_zip_code}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>

                  {/* Contact Phone / Ext / Fax */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col className="flex-g-1 p-l-5 pe-0">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Phone: (###) ###-####"
                        name="contact_phone"
                        value={formattedContactPhone}
                        onChange={makePhoneHandler("contact_phone", setFormattedContactPhone)}
                      />
                    </Col>
                    <Col
                      style={{
                        width: "110px",
                        maxWidth: "110px",
                      }}
                      className="p-r-0 p-l-5"
                    >
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Ext"
                        name="contact_extension"
                        value={formik.values.contact_extension}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col className="flex-g-1 p-r-5 p-l-5">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Fax: (###) ###-####"
                        name="contact_fax"
                        value={formattedContactFax}
                        onChange={makePhoneHandler("contact_fax", setFormattedContactFax)}
                      />
                    </Col>
                  </Row>

                  {/* Contact Email / Website */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col sm={12} className="p-l-5 p-r-5">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Email: someone@contact.com"
                        name="contact_email"
                        value={formik.values.contact_email}
                        onChange={formik.handleChange}
                        isInvalid={formik.touched.contact_email && !!formik.errors.contact_email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.contact_email}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col sm={12} className="p-r-5 p-l-5">
                      <Form.Control
                        type="text"
                        className="height-25 p-0 p-l-5 rounded-0"
                        placeholder="Website: www.contact.com"
                        name="contact_website"
                        value={formik.values.contact_website}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>
                </Tab.Pane>

                {/* ─── LOAN DETAILS TAB ─── */}
                <Tab.Pane
                  style={{
                    minHeight: "225px",
                  }}
                  eventKey="loanDetails"
                >
                  {/* Disbursed Date */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Form.Label
                      column
                      sm={2}
                      style={{
                        color: "var(--primary-25)",
                        fontWeight: "600",
                      }}
                      className="fw-bold p-l-5 white-space-nw height-25 d-flex align-items-center py-0"
                    >
                      Disbursed Date:
                    </Form.Label>
                    <Col sm={10} className="p-l-0 p-r-5">
                      <Form.Control
                        type="date"
                        className="height-25 p-0 p-l-5 rounded-0"
                        name="date_disbursed"
                        {...formik.getFieldProps("date_disbursed")}
                        style={{ appearance: "none" }}
                        onClick={(e) => e.target.showPicker()}
                        isInvalid={formik.touched.date_disbursed && !!formik.errors.date_disbursed}
                      />
                    </Col>
                  </Row>

                  {/* Loan Amount / Fees */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Form.Label
                      column
                      sm={2}
                      style={{
                        color: "var(--primary-25)",
                        fontWeight: "600",
                      }}
                      className="fw-bold p-l-5 white-space-nw height-25 d-flex align-items-center py-0"
                    >
                      Loan Amount:
                    </Form.Label>
                    <Col sm={4} className="p-l-0 p-r-0">
                      <Form.Control
                        type="text"
                        name="current_amount_verified"
                        className="monospace-font height-25 p-0 p-l-5 rounded-0"
                        value={currencyDisplay("current_amount_verified")}
                        onFocus={() => formik.setFieldValue("current_amount_verified", "")}
                        onChange={(e) => handleCurrencyChange(e, "current_amount_verified")}
                        onBlur={() => handleCurrencyBlur("current_amount_verified")}
                        isInvalid={
                          formik.touched.current_amount_verified &&
                          !!formik.errors.current_amount_verified
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.current_amount_verified}
                      </Form.Control.Feedback>
                    </Col>
                    <Form.Label
                      column
                      sm={2}
                      style={{
                        color: "var(--primary-25)",
                        fontWeight: "600",
                      }}
                      className="fw-bold p-l-15 white-space-nw height-25 d-flex align-items-center py-0"
                    >
                      Fees:
                    </Form.Label>
                    <Col sm={4} className="p-l-15 p-r-5">
                      <Form.Control
                        type="text"
                        name="fees"
                        className="monospace-font height-25 p-0 p-l-5 rounded-0"
                        value={currencyDisplay("fees")}
                        onFocus={() => formik.setFieldValue("fees", "")}
                        onChange={(e) => handleCurrencyChange(e, "fees")}
                        onBlur={() => handleCurrencyBlur("fees")}
                        isInvalid={formik.touched.fees && !!formik.errors.fees}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.fees}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  {/* Interest / Payoff Estimate */}
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Form.Label
                      column
                      sm={2}
                      style={{
                        color: "var(--primary-25)",
                        fontWeight: "600",
                      }}
                      className="fw-bold p-l-5 white-space-nw height-25 d-flex align-items-center py-0"
                    >
                      Interest:
                    </Form.Label>
                    <Col sm={4} className="p-l-0 p-r-0">
                      <div className="position-relative d-flex align-items-center">
                        <Form.Control
                          type="text"
                          name="interest"
                          className="monospace-font height-25 p-0 p-l-5 rounded-0"
                          placeholder="0.00"
                          style={{ paddingRight: "20px" }}
                          value={formik.values.interest}
                          onChange={(e) => {
                            let value = e.target.value.replace(/[^0-9.]/g, "");
                            if ((value.match(/\./g) || []).length > 1) {
                              value = value.substring(0, value.lastIndexOf("."));
                            }
                            if (value.includes(".")) {
                              const parts = value.split(".");
                              if (parts[1].length > 2) parts[1] = parts[1].substring(0, 2);
                              value = parts.join(".");
                            }
                            formik.setFieldValue("interest", value);
                          }}
                          onBlur={() => {
                            const raw = parseFloat(formik.values.interest || 0);
                            formik.setFieldValue("interest", isNaN(raw) ? "0.00" : raw.toFixed(2));
                          }}
                          isInvalid={formik.touched.interest && !!formik.errors.interest}
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.interest}
                      </Form.Control.Feedback>
                    </Col>
                    <Form.Label
                      column
                      sm={2}
                      style={{
                        color: "var(--primary-25)",
                        fontWeight: "600",
                      }}
                      className="fw-bold p-l-15 white-space-nw height-25 d-flex align-items-center py-0"
                    >
                      Final Payoff:
                    </Form.Label>
                    <Col sm={4} className="p-l-15 p-r-5">
                      <Form.Control
                        type="text"
                        name="final_payoff"
                        className="monospace-font height-25 p-0 p-l-5 rounded-0"
                        value={currencyDisplay("final_payoff")}
                        onFocus={() => formik.setFieldValue("final_payoff", "")}
                        onChange={(e) => handleCurrencyChange(e, "final_payoff")}
                        onBlur={() => handleCurrencyBlur("final_payoff")}
                        isInvalid={formik.touched.final_payoff && !!formik.errors.final_payoff}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.final_payoff}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                  <div className="d-flex align-items-center justify-content-start">
                    <Form.Label
                      column
                      sm={3}
                      style={{
                        color: "var(--primary-25)",
                        fontWeight: "600",
                        minWidth: "200px",
                      }}
                      className="fw-bold p-l-5 p-r-5 white-space-nw height-25 d-flex align-items-center py-0"
                    >
                      Final Payoff Recorded Date:
                    </Form.Label>
                    {formatDateForPanelDisplay(loan?.final_payoff_recorded || "")}
                  </div>
                  {loan?.final_payoff_recorded && (
                    <div
                      className="p-l-5 p-r-5 d-flex align-items-center "
                      style={{
                        color: "var(--primary-50)",
                        fontWeight: "600",
                      }}
                    >
                      Final Payoff amount has been recorded on the Settle page for this loan. To
                      change this amount, input a new value in to the Final Payoff field, save the
                      changes, and re-record the amount in the Loan panel.
                    </div>
                  )}
                  {/* Final Payoff */}
                  {/* <Row className="align-items-center custom-margin-bottom mx-0">
                    <Form.Label
                      column
                      sm={2}
                      style={{
                        color: "var(--primary-25)",
                        fontWeight: "600",
                      }}
                      className="fw-bold p-l-5 p-r-0 white-space-nw height-25 d-flex align-items-center py-0"
                    >
                      Final Payoff:
                    </Form.Label>
                    <Col sm={10} className="p-r-5 p-l-0">
                      <Form.Control
                        type="text"
                        name="final_payoff"
                        className="monospace-font height-25 p-0 p-l-5 rounded-0"
                        value={currencyDisplay("final_payoff")}
                        onFocus={() => formik.setFieldValue("final_payoff", "")}
                        onChange={(e) => handleCurrencyChange(e, "final_payoff")}
                        onBlur={() => handleCurrencyBlur("final_payoff")}
                        isInvalid={formik.touched.final_payoff && !!formik.errors.final_payoff}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.final_payoff}
                      </Form.Control.Feedback>
                    </Col>
                  </Row> */}

                  {/* Verified Date */}
                  {/* <Row className="align-items-center custom-margin-bottom mx-0 mb-0">
                    <Form.Label
                      column
                      sm={2}
                      style={{
                        color: "var(--primary-25)",
                        fontWeight: "600",
                      }}
                      className="fw-bold p-l-5 p-r-0 white-space-nw height-25 d-flex align-items-center py-0"
                    >
                      Verified Date:
                    </Form.Label>
                    <Col sm={10} className="p-r-5 p-l-0">
                      <Form.Control
                        type="date"
                        className="height-25 p-0 p-l-5 rounded-0"
                        name="date_verified"
                        {...formik.getFieldProps("date_verified")}
                        style={{ appearance: "none" }}
                        onClick={(e) => e.target.showPicker()}
                        isInvalid={formik.touched.date_verified && !!formik.errors.date_verified}
                      />
                    </Col>
                  </Row> */}
                </Tab.Pane>

                {/* ─── FOOTER ─── */}
                <div className="d-flex justify-content-between" style={{ padding: "5px" }}>
                  <Button
                    variant="secondary"
                    className="height-25"
                    style={{ padding: "0px 12px" }}
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={handleDeleteLoan}
                    className="m-l-5 bg-danger height-25"
                    style={{ padding: "0px 12px" }}
                  >
                    Delete
                  </Button>
                  <Button
                    type="submit"
                    variant="success"
                    className="m-l-5 bg-success height-25"
                    style={{ padding: "0px 12px" }}
                  >
                    Save
                  </Button>
                </div>
              </Tab.Content>
            </Form>
          </Tab.Container>
        </Modal.Body>
      </Modal>

      {companyStateShow && (
        <SelectStateModal
          show={companyStateShow}
          handleClose={() => setCompanyStateShow(false)}
          onChange={(state) => {
            formik.setFieldValue("company_state", state.StateAbr);
            setSelectedCompanyState(state.StateAbr);
          }}
          statesData={statesAbrs}
        />
      )}

      {contactStateShow && (
        <SelectStateModal
          show={contactStateShow}
          handleClose={() => setContactStateShow(false)}
          onChange={(state) => {
            formik.setFieldValue("contact_state", state.StateAbr);
            setSelectedContactState(state.StateAbr);
          }}
          statesData={statesAbrs}
        />
      )}
    </>
  );
};

export default EditCaseLoanModal;
